import { useState, useEffect, useCallback } from 'react';

// Types
interface CitationDrawerState {
  visible: boolean;
  citations: Array<{
    title?: string;
    url?: string;
    text?: string;
    cited_text?: string;
    sentence?: string;
  }>;
  selectedIndex: number;
  mode: 'inline' | 'drawer';
  anchorElement: HTMLElement | null;
}

interface ChatMessage {
  message_id?: string;
  id?: string;
  citation_sources?: Array<{
    title?: string;
    url?: string;
    text?: string;
    cited_text?: string;
    sentence?: string;
  }>;
  metadata?: {
    citations?: Array<{
      title?: string;
      url?: string;
      text?: string;
      cited_text?: string;
      sentence?: string;
    }>;
    citation_sources?: Array<{
      title?: string;
      url?: string;
      text?: string;
      cited_text?: string;
      sentence?: string;
    }>;
  };
}

/**
 * Mobile-optimized citation system hook
 * Provides touch-friendly citation interactions
 */
export const useMobileCitations = (messages: ChatMessage[]) => {
  const [citationDrawer, setCitationDrawer] = useState<CitationDrawerState>({
    visible: false,
    citations: [],
    selectedIndex: 0,
    mode: 'drawer',
    anchorElement: null
  });

  // Detect mobile device
  const isMobile = useCallback(() => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
           window.innerWidth <= 768 || 
           ('ontouchstart' in window);
  }, []);

  // Handle citation interaction
  const handleCitationInteraction = useCallback((event: Event) => {
    const target = event.target as HTMLElement;
    if (!target.classList.contains('citation-marker')) return;

    // Prevent default behavior
    event.preventDefault();
    event.stopPropagation();

    const citationNumber = target.getAttribute('data-source-number');
    const messageElement = target.closest('.response-content-direct');
    const messageId = messageElement?.getAttribute('data-message-id');

    if (citationNumber && messageId) {
      const message = messages.find(m => {
        const msgId = m.message_id || m.id;
        return msgId && msgId.toString() === messageId;
      });

      // Check both metadata.citations and citation_sources for compatibility
      const rawCitations = message?.metadata?.citations || message?.citation_sources || message?.metadata?.citation_sources;
      
      // Filter out null/undefined citations to prevent errors
      const validCitations = rawCitations?.filter(citation => 
        citation && typeof citation === 'object'
      ) || [];

      if (validCitations.length > 0) {
        const selectedIndex = parseInt(citationNumber) - 1;
        
        // Determine interaction mode based on device and context
        const mode = isMobile() ? 'drawer' : 'inline';
        
        setCitationDrawer({
          visible: true,
          citations: validCitations,
          selectedIndex: Math.max(0, Math.min(selectedIndex, validCitations.length - 1)),
          mode,
          anchorElement: mode === 'inline' ? target : null
        });
      }
    }
  }, [messages, isMobile]);

  // Handle citation selection
  const handleCitationSelect = useCallback((index: number) => {
    setCitationDrawer(prev => ({
      ...prev,
      selectedIndex: index
    }));
  }, []);

  // Close citation drawer
  const closeCitationDrawer = useCallback(() => {
    setCitationDrawer({
      visible: false,
      citations: [],
      selectedIndex: 0,
      mode: 'drawer',
      anchorElement: null
    });
  }, []);

  // Set up event listeners
  useEffect(() => {
    // Remove any existing hover-based citation listeners
    const existingMarkers = document.querySelectorAll('.citation-marker');
    existingMarkers.forEach(marker => {
      // Remove old hover events
      marker.removeAttribute('onmouseover');
      marker.removeAttribute('onmouseout');
      
      // Remove any existing event listeners by cloning and replacing
      const newMarker = marker.cloneNode(true);
      marker.parentNode?.replaceChild(newMarker, marker);
    });

    // Add new touch/click-optimized listeners
    const citationMarkers = document.querySelectorAll('.citation-marker');
    citationMarkers.forEach(marker => {
      // Use touchstart for immediate response on mobile, click for desktop
      if (isMobile()) {
        marker.addEventListener('touchstart', handleCitationInteraction, { passive: false });
        marker.addEventListener('click', handleCitationInteraction);
      } else {
        marker.addEventListener('click', handleCitationInteraction);
      }

      // Add visual feedback for touch
      marker.addEventListener('touchstart', () => {
        marker.classList.add('citation-marker-pressed');
      });
      
      marker.addEventListener('touchend', () => {
        marker.classList.remove('citation-marker-pressed');
      });

      marker.addEventListener('touchcancel', () => {
        marker.classList.remove('citation-marker-pressed');
      });
    });

    // Cleanup function
    return () => {
      citationMarkers.forEach(marker => {
        marker.removeEventListener('touchstart', handleCitationInteraction);
        marker.removeEventListener('click', handleCitationInteraction);
      });
    };
  }, [messages, handleCitationInteraction, isMobile]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && citationDrawer.visible) {
        closeCitationDrawer();
      }
    };

    if (citationDrawer.visible) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [citationDrawer.visible, closeCitationDrawer]);

  // Handle window resize to adjust mode
  useEffect(() => {
    const handleResize = () => {
      if (citationDrawer.visible) {
        const newMode = isMobile() ? 'drawer' : 'inline';
        if (newMode !== citationDrawer.mode) {
          setCitationDrawer(prev => ({
            ...prev,
            mode: newMode,
            anchorElement: newMode === 'inline' ? prev.anchorElement : null
          }));
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [citationDrawer.visible, citationDrawer.mode, isMobile]);

  return {
    citationDrawer,
    handleCitationSelect,
    closeCitationDrawer,
    isMobile: isMobile()
  };
};